import React, { useState, useEffect } from "react";
import { adminGetUserById } from "../api/admin";
import { toast } from "react-toastify";
import { FaUser, FaWallet, FaShoppingBag, FaEnvelope, FaPhoneAlt, FaCalendarAlt, FaCheckCircle, FaExclamationCircle, FaDownload } from "react-icons/fa";
import { adminGetSettings } from "../api/admin";
import UserReportModal from "../components/UserReportModal";
import "./UserDetailsModal.css"; 

const UserDetailsView = ({ userId }) => {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (userId) {
            fetchUserDetails();
        }
    }, [userId]);

    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            const response = await adminGetUserById(userId);
            if (response.data && response.data.success) {
                setUserData(response.data);
            } else {
                toast.error("Failed to load user details");
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
            toast.error("Error loading user details");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-container" style={{ minHeight: '400px' }}>
                <div className="spinner"></div>
                <p>Retrieving user comprehensive data...</p>
            </div>
        );
    }

    if (!userData) {
        return <div className="error-container">User data not available.</div>;
    }

    return (
        <div className="user-details-full-view">
            <div className="details-grid">
                {/* Left Column: Profile Card */}
                <div className="details-left-col">
                    <div className="profile-highlight-card">
                        <div className="profile-avatar">
                            {userData.user?.name?.[0]?.toUpperCase() || "U"}
                        </div>
                        <h3 className="profile-name">{userData.user?.name || "N/A"}</h3>
                        <div className={`profile-status-badge ${userData.user?.isVerified ? 'verified' : 'unverified'}`}>
                            {userData.user?.isVerified ? <FaCheckCircle /> : <FaExclamationCircle />}
                            {userData.user?.isVerified ? 'Active Account' : 'Inactive Account'}
                        </div>
                        
                        <div className="wallet-card-inline">
                            <div className="wallet-label">
                                <FaWallet /> Wallet Balance
                            </div>
                            <div className="wallet-amount">
                                ₹{(userData.user?.walletBalance || 0).toLocaleString()}
                            </div>
                        </div>
                    </div>

                    <div className="contact-info-block">
                        <h4 className="block-title">Contact & Security</h4>
                        <div className="info-item">
                            <span className="info-key"><FaEnvelope /> Email</span>
                            <span className="info-val">{userData.user?.email || "N/A"}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-key"><FaPhoneAlt /> Mobile</span>
                            <span className="info-val">{userData.user?.mobile || "N/A"}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-key"><FaCalendarAlt /> Joined Date</span>
                            <span className="info-val">{new Date(userData.user?.createdAt).toLocaleDateString(undefined, {
                                day: 'numeric', month: 'long', year: 'numeric'
                            })}</span>
                        </div>
                    </div>
                </div>

                {/* Right Column: Order History */}
                <div className="details-right-col">
                    <div className="orders-history-block">
                        <div className="block-header">
                            <h4 className="block-title"><FaShoppingBag /> Order History</h4>
                            <span className="count-tag">{userData.orders?.length || 0} Orders</span>
                        </div>
                        
                        <div className="order-history-table-wrapper">
                            {userData.orders && userData.orders.length > 0 ? (
                                <table className="order-history-full-table">
                                    <thead>
                                        <tr>
                                            <th>Order ID</th>
                                            <th>Date</th>
                                            <th>Method</th>
                                            <th>Total</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {userData.orders.map((order) => (
                                            <tr key={order.id || order._id}>
                                                <td className="order-id-cell">#{String(order.id || order._id).slice(-8).toUpperCase()}</td>
                                                <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                                                <td>{order.paymentMethod || 'N/A'}</td>
                                                <td className="amount-cell">₹{(order.totalAmount || order.totalPrice || 0).toLocaleString()}</td>
                                                <td>
                                                    <span className={`status-tag status-${(order.status || 'pending').toLowerCase()}`}>
                                                        {order.status || 'Pending'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="no-orders-illustration">
                                    <FaShoppingBag className="icon" />
                                    <p>No orders recorded for this user yet.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetailsView;
